// The installed build-time copy of the Kielioppi tables (./forms.ts explains it): the § files that
// grammar/tables.ts loaded, and which word's cells may be used. No engine code here, so the eager
// grammar index (./index.ts, ./tables.ts) does not pull the paradigm generator into the start-up chunk.
import type { Form as HvForm, Lemma } from '@valyrio/engine/types';

/** One word's cells in a § file. */
export interface GrammarTableWord {
  /** lemmaPrint(lemma) of the word the cells were built from. */
  fp: string;
  confident: boolean;
  /** Raw cell key as the MDX asks for it (<Form k>, cellKeys) → generatedFormsFor(lemma, key).forms. */
  cells: Record<string, HvForm[]>;
}

/** app/public/data/grammar/<theme>.json */
export interface GrammarTables {
  v: 1;
  theme: string;
  /** The grammar hash of the build that wrote it (app/scripts/grammar-hash.ts). */
  engine: string;
  /** The bundle the cells were built from (for people; the words' `fp` decide). */
  bundle: string;
  words: Record<string, GrammarTableWord>;
  /** What the file was built from (code, bundle, § sources): the build skips an unchanged one. */
  input?: string;
}

/** Fields of a word that never change its forms (meanings, notes, links); everything else counts. */
const NOT_FORMS = new Set([
  'senses',
  'etymology',
  'derived',
  'categories',
  'audio',
  'showRefs',
  'duolingo',
  'notes',
  'source',
  'status',
  'wikiRev',
  'supersededBy',
  'supersededAt',
  // an own loan's source word (16b §1)
  'loan',
]);

/** A hash of the word as the engine sees it when it generates its forms (FNV-1a, 2 × 32 bit). */
export function lemmaPrint(lemma: Lemma): string {
  // only the word's own fields are left out (a form inside its paradigm keeps all of its own)
  const s = JSON.stringify(lemma, function (this: unknown, k: string, v: unknown) {
    return this === lemma && NOT_FORMS.has(k) ? undefined : v;
  });
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x5bd1e995);
  }
  return (a >>> 0).toString(36) + (b >>> 0).toString(36);
}

const installed = new Map<string, GrammarTableWord>();
let checked = new WeakMap<Lemma, GrammarTableWord | null>();

/** Install a § file (already checked for this build's hash: grammar/tables.ts). */
export function installGrammarTables(t: GrammarTables): void {
  for (const [id, w] of Object.entries(t.words)) {
    const have = installed.get(id);
    installed.set(id, have && have.fp === w.fp ? { ...have, cells: { ...have.cells, ...w.cells } } : w);
  }
  checked = new WeakMap();
}

/** Forget every installed § (tests). */
export function resetGrammarTables(): void {
  installed.clear();
  checked = new WeakMap();
}

/** The installed cells of `lemma`, when they were built from this very word. */
export function tableOf(lemma: Lemma): GrammarTableWord | null {
  if (!installed.size) return null;
  let w = checked.get(lemma);
  if (w === undefined) {
    const t = installed.get(lemma.id);
    w = t && t.fp === lemmaPrint(lemma) ? t : null;
    checked.set(lemma, w);
  }
  return w;
}
